import React, { useState, useEffect } from 'react';
// import MUI components
import { Grid2 as Grid, Alert, Snackbar } from '@mui/material';
// import custom components
import { BoxCenterStart } from '../style/box';
import { TypographyBold } from '../style/typography';
import { commonStyles } from "../style/common";
import CommonCard from '../components/common_card';
import { BaseDialog } from '../components/base_dialog';
import UserRegistration from "../components/user_registration";
import UserList from '../components/user_list';
import { validationSchemaRegistUser, validationSchemaEditUser } from '../func/validation_check';
import { fetchData, postData } from "../func/api";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import SkeletonLoading from "../components/skeleton_loading";
import { useAuth } from '../contexts/AuthContext';
import { REPORT_STATUS } from '../const/config';

const MstUserList = () => {
    const [users, setUsers] = useState([]);
    const [departmentList, setDepartmentList] = useState([]);
    const [roleList, setRoleList] = useState([]);
    const [expandedUser, setExpandedUser] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState(null);
    const [dialogData, setDialogData] = useState(null);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState("success");
    const [refreshFlag, setRefreshFlag] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isDisabled, setIsDisabled] = useState(false);
    const { currentUser, setCurrentUser } = useAuth();

    const { control: registControl, handleSubmit: handleRegistSubmit, reset: registReset } = useForm({
        resolver: yupResolver(validationSchemaRegistUser(users)),
        defaultValues: {
            registUser: {
                name: "",
                tel: "",
                email: "",
                password: "",
                password_confirm: "",
                department: "",
                role: ""
            }
        },
        mode: "onBlur"
    });

    const { control: editControl, handleSubmit: handleEditSubmit, reset: editReset } = useForm({
        resolver: yupResolver(validationSchemaEditUser),
        defaultValues: {
            editUser: {
                name: "",
                tel: "",
                email: "",
                password: "",
                password_confirm: "",
                department: "",
                role: ""
            }
        },
        mode: "onBlur",
        context: { users }
    });

    // データ取得
    useEffect(() => {
        const fetchDataAll = async () => {
            try {
                const [userList, departmentList, roleList] = await Promise.all([
                    fetchData(`api/get_user_list`),
                    fetchData(`api/get_department_list`),
                    fetchData(`api/get_role_list`)
                ]);

                setUsers(userList); // ユーザー一覧
                setDepartmentList(departmentList); // 所属一覧
                setRoleList(roleList); // 権限一覧

                // 地震一覧データ取得
                const earthquakeData = await fetchData(`api/get_earthquake_list/`);
                // 業務対中の場合は、編集不可
                const reportingData = earthquakeData.find(obj =>
                    obj.report_status_uuid === REPORT_STATUS.UNSUPPORTED ||
                    obj.report_status_uuid === REPORT_STATUS.FISRTREPORT ||
                    obj.report_status_uuid === REPORT_STATUS.PRIMARYCHECK ||
                    obj.report_powerplant_status_uuid === REPORT_STATUS.POWERPLANTSUPPORT
                );
                setIsDisabled(reportingData ? true : false);

            } catch (error) {
                console.error("データ取得中にエラーが発生しました:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchDataAll();
    }, [refreshFlag]);

    // 編集対象のユーザーが変わったらフォームをリセット
    useEffect(() => {
        if (editUser) {
            editReset({
                editUser: {
                    uuid: editUser.uuid,
                    name: editUser.name,
                    tel: editUser.tel,
                    email: editUser.email,
                    password: editUser.password,
                    password_confirm: editUser.password_confirm,
                    department: editUser.department,
                    role: editUser.role
                }
            });
        }
    }, [editUser, editReset]);

    const openDialog = (type, data = null) => {
        setDialogType(type);
        setDialogData(data);
        setDialogOpen(true);
    };

    const handleCancelEdit = () => {
        setExpandedUser(false);
        setEditUser(null);
    };

    const handleDialogConfirm = () => {
        switch (dialogType) {
            case 'register':
                handleRegistSubmit(handleConfirm)(); // 登録処理
                break;
            case 'delete':
                handleDeleteConfirm(); // 削除処理
                break;
            case 'save':
                handleSaveConfirm(); // 保存処理
                break;
            default:
                break;
        }
        setDialogOpen(false);
    };

    const handleDialogCancel = () => {
        setDialogOpen(false);
    };

    // 「ユーザーを登録」ボタンのダイアログ「OK」押下時
    const handleConfirm = async () => {
        try {
            // パスワードをSHA-256でハッシュ化
            const encoder = new TextEncoder();
            const dataBuffer = encoder.encode(dialogData.password);
            const hashBuffer = await crypto.subtle.digest("SHA-256", dataBuffer);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const hashedPassword = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

            const data = {
                name: dialogData.name,
                tel: dialogData.tel,
                email: dialogData.email,
                password: hashedPassword,
                department: dialogData.department,
                role: dialogData.role
            };

            await postData("api/post_regist_user", data);
            setAlertMessage("登録が完了しました");
            setAlertSeverity("success");
            setAlertOpen(true);
            setRefreshFlag(prev => !prev);
            registReset();
        } catch (err) {
            setAlertMessage("登録に失敗しました");
            setAlertSeverity("error");
            setAlertOpen(true);
        }
    };

    const handleEditClick = (user) => {
        setEditUser({
            uuid: user.uuid,
            name: user.name,
            tel: user.tel || '',
            email: user.email,
            password: '',
            password_confirm: '',
            department: user.department_uuid || '',
            role: user.role_uuid || ''
        });
        setExpandedUser(user.uuid);
    };

    const handleDeleteClick = (user) => {
        if (users.length === 1) {
            setAlertMessage("ユーザーは最低1人必要です。");
            setAlertSeverity("error");
            setAlertOpen(true);
            return;
        }
        openDeleteDialog(user);
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const openDeleteDialog = (user) => {
        setDialogType("delete");
        setDialogData(user); // 削除対象
        setDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        setUsers(
            users.filter((user) => user.uuid !== userToDelete.uuid)
        );
        setDeleteDialogOpen(false);
        setUserToDelete(null);

        // ユーザー論理削除処理
        try {
            // 削除前データを履歴テーブルに登録
            await postData("api/post_insert_user_history", userToDelete);

            const data = {
                uuid: userToDelete.uuid
            };

            await postData("api/post_delete_user", data);

            setAlertMessage("ユーザーの削除が完了しました。");
            setAlertSeverity("success");
            setAlertOpen(true);
            // 削除後にデータを再取得
            setRefreshFlag(prev => !prev);
        } catch (err) {
            console.error("削除失敗しました。：", err);
            setAlertMessage("ユーザーの削除に失敗しました。再度時間をおいてからお試しください。");
            setAlertSeverity("error");
            setAlertOpen(true);
        } finally {
            setDeleteDialogOpen(false);
            setUserToDelete(null);
            setExpandedUser(false);
            setEditUser(null);
        }
    };

    const handleSaveConfirm = async () => {
        try {
            // 変更前データを取得
            const beforeData = users.find(u => u.uuid === dialogData.uuid);

            if (beforeData) {
                // 履歴テーブルに登録（変更前データ）
                await postData("api/post_insert_user_history", beforeData);
            }

            // パスワードが入力されていればSHA-256でハッシュ化
            let hashedPassword = dialogData.password;

            if (dialogData.password) {
                const encoder = new TextEncoder();
                const dataBuffer = encoder.encode(dialogData.password);
                const hashBuffer = await crypto.subtle.digest("SHA-256", dataBuffer);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                hashedPassword = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
            }

            const data = {
                uuid: dialogData.uuid,
                name: dialogData.name,
                tel: dialogData.tel,
                email: dialogData.email,
                password: hashedPassword,
                department: dialogData.department,
                role: dialogData.role
            };

            await postData("api/post_update_user", data);

            setUsers(prevUsers => {
                const index = prevUsers.findIndex(u => u.uuid === dialogData.uuid);
                if (index === -1) return prevUsers;

                const updatedUser = {
                    ...prevUsers[index],
                    ...dialogData
                };

                const newUsers = [...prevUsers];
                newUsers[index] = updatedUser;
                return newUsers;
            });

            setAlertMessage("ユーザーの更新が完了しました。");
            setAlertSeverity("success");
            setAlertOpen(true);
            setRefreshFlag(prev => !prev);

            // 編集したユーザーがログインユーザーならセッション情報（名前、権限、所属）を更新
            if (dialogData.uuid === currentUser.id) {
                setCurrentUser({
                    ...currentUser,
                    name: dialogData.name,
                    role: dialogData.role,
                    department: dialogData.department
                });
            }
        } catch (err) {
            console.error("更新失敗しました：", err);
            setAlertMessage("ユーザーの更新に失敗しました。");
            setAlertSeverity("error");
            setAlertOpen(true);
        } finally {
            setExpandedUser(false);
            setEditUser(null);
            setDialogOpen(false);
        }
    };

    if (loading) {
        return <SkeletonLoading />;
    }

    return (
        <BoxCenterStart sx={commonStyles.page}>
            <Grid
                container
                spacing={7}
                columns={12}
                sx={commonStyles.container}
            >
                {/* Page Title */}
                <Grid size={{ xs: 12 }}>
                    <CommonCard
                        size={{ xs: 12 }}
                        child={
                            <TypographyBold
                                sx={{ textAlign: 'center', padding: '10px 0' }}
                            >
                                ユーザー管理
                            </TypographyBold>
                        }
                    ></CommonCard>
                </Grid>
                {/* 新規ユーザー登録 */}
                <UserRegistration
                    control={registControl}
                    departmentList={departmentList}
                    roleList={roleList}
                    openDialog={openDialog}
                    handleRegistSubmit={handleRegistSubmit}
                    expanded={true}
                    isDisabled={isDisabled}
                />
                {/* ユーザー一覧 */}
                <UserList
                    control={editControl}
                    users={users}
                    expandedUser={expandedUser}
                    departmentList={departmentList}
                    roleList={roleList}
                    handleEditClick={handleEditClick}
                    handleCancelEdit={handleCancelEdit}
                    handleDeleteClick={handleDeleteClick}
                    handleEditSubmit={handleEditSubmit}
                    openDialog={openDialog}
                    isDisabled={isDisabled}
                />
                {/* 確認ダイアログ */}
                <BaseDialog
                    open={dialogOpen}
                    onClose={handleDialogCancel}
                    title={
                        dialogType === "register" ? "ユーザー登録" :
                            dialogType === "delete" ? "ユーザー削除" :
                                dialogType === "save" ? "ユーザー保存" : ""
                    }
                    message={
                        dialogType === "register" ? "ユーザーを登録します。よろしいですか？" :
                            dialogType === "delete" ? `ユーザー「${dialogData?.name}」を削除しますか？\nこの操作は取り消せません。` :
                                dialogType === "save" ? "編集内容を保存します。よろしいですか？" : ""
                    }
                    onConfirm={handleDialogConfirm}
                    confirmText={
                        dialogType === "register" ? "OK" :
                            dialogType === "delete" ? "削除" :
                                dialogType === "save" ? "保存" : "OK"
                    }
                    cancelText="キャンセル"
                />
                {/* 失敗時のSnackbar */}
                {alertMessage && (
                    <Snackbar
                        open={alertOpen}
                        autoHideDuration={3000}
                        onClose={() => setAlertOpen(false)}
                        anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    >
                        <Alert severity={alertSeverity} onClose={() => setAlertOpen(false)}>
                            {alertMessage}
                        </Alert>
                    </Snackbar>
                )}
            </Grid>
        </BoxCenterStart>
    );
};

export default MstUserList;